import { differenceInCalendarDays, getDay } from "date-fns";

// On the regular request form every picked day is a possible ARRIVAL, and
// the nights are the minimum stay from each. People often pick the days of
// the stay instead, and then get alerts for stays that start on their
// check-out day. These rules only fire on shapes that request data shows
// are mistakes: people with these picks fix them after a wrong-day alert
// 20-31% of the time, against 5% for deliberate picks such as several
// Fridays. Picking every night of the stay (Fri + Sat, 2 nights) is left
// alone: "either day" is common there, and those people fix it no more
// often than deliberate picks.

// Longer stays are left alone: "this week or next" (Sat + the next Sat for
// 7 nights) is a plausible deliberate pick.
const MAX_CHECKOUT_NIGHTS = 6;
const MAX_PAIR_GAP = 4;
const SUNDAY = 0;

function sortDays(dates) {
  return [...dates].sort((a, b) => a - b);
}

/**
 * The picked days end on the check-out day of a stay that starts on the
 * first one: the last day is exactly `nights` after the first (Fri + Sun
 * for 2 nights, Fri + Mon for 3, or Fri + Sat + Sun for 2).
 */
function findCheckoutDay(dates, nights) {
  if (dates.length < 2 || nights < 2 || nights > MAX_CHECKOUT_NIGHTS) {
    return null;
  }
  const sorted = sortDays(dates);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  return differenceInCalendarDays(last, first) === nights ? last : null;
}

/**
 * Two days that read as arrive and leave, with the nights left one short:
 * Fri + Sun with 1 night means a Friday-to-Sunday stay of 2 nights.
 */
function findArrivalDeparture(dates, nights) {
  if (dates.length !== 2) return null;
  const [arrival, departure] = sortDays(dates);
  const gap = differenceInCalendarDays(departure, arrival);
  if (gap < 2 || gap > MAX_PAIR_GAP || gap !== nights + 1) return null;
  return { arrival, departure, nights: gap };
}

/**
 * Saturday + Sunday for 1 night: often Saturday night with Sunday as the
 * check-out day, but sometimes "either night". Only worth a question.
 */
function findSundayCheckout(dates, nights) {
  if (dates.length !== 2 || nights !== 1) return null;
  const [first, second] = sortDays(dates);
  if (differenceInCalendarDays(second, first) !== 1) return null;
  return getDay(second) === SUNDAY ? second : null;
}

/**
 * The hint to show for the picked arrival days and nights, or null.
 *   { kind: "checkout-day", checkoutDay, arrival, nights }
 *   { kind: "arrival-departure", arrival, departure, nights }
 *   { kind: "sunday-checkout", checkoutDay }
 */
export function getStayShapeHint(dates, nights) {
  if (!dates?.length || !(nights > 0)) return null;

  const checkoutDay = findCheckoutDay(dates, nights);
  if (checkoutDay) {
    return {
      kind: "checkout-day",
      checkoutDay,
      arrival: sortDays(dates)[0],
      nights,
    };
  }

  const pair = findArrivalDeparture(dates, nights);
  if (pair) return { kind: "arrival-departure", ...pair };

  const sunday = findSundayCheckout(dates, nights);
  if (sunday) return { kind: "sunday-checkout", checkoutDay: sunday };

  return null;
}
